import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import config from "../../../config";
import Swal from "sweetalert2";

const AssignPeriodModal = forwardRef(
  ({ classId, academicYear, onSuccess }, ref) => {
    const [show, setShow] = useState(false);

    const [day, setDay] = useState("");
    const [period, setPeriod] = useState("");
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState("");
    const [teacherName, setTeacherName] = useState("");
    const [loading, setLoading] = useState(false);

    /* ---------------------------------------
       Expose openModal
    --------------------------------------- */
    useImperativeHandle(ref, () => ({
      openModal(dayValue, periodValue) {
        if (!classId) return;

        setDay(dayValue);
        setPeriod(periodValue);
        setSelectedSubject("");
        setTeacherName("");
        setShow(true);
      },
    }));

    /* ---------------------------------------
       Fetch subjects when modal opens OR
       when classId changes (section changed)
    --------------------------------------- */
    useEffect(() => {
      if (show && classId) {
        fetchSubjects();
      }
    }, [show, classId]);

    const fetchSubjects = async () => {
      if (!classId) return;

      try {
        const res = await axios.get(
          `${config.apiBaseUrl}/academic/get-subjects-by-class.php`,
          {
            params: { class_id: classId },
          }
        );

        if (res.data.success) {
          setSubjects(res.data.data || []);
        } else {
          setSubjects([]);
        }
      } catch (err) {
        console.error("Failed to load subjects");
        setSubjects([]);
      }
    };

    /* ---------------------------------------
       Get Assigned Teacher
    --------------------------------------- */
    const handleSubjectChange = async (subjectId) => {
      setSelectedSubject(subjectId);
      setTeacherName("");

      if (!subjectId || !classId) return;

      try {
        const res = await axios.get(
          `${config.apiBaseUrl}/academic/get-assigned-teacher.php`,
          {
            params: {
              class_id: classId,
              subject_id: subjectId,
              academic_year: academicYear,
            },
          }
        );

        if (res.data.success) {
          setTeacherName(res.data.teacher_name);
        } else {
          setTeacherName("No teacher assigned");
        }
      } catch (err) {
        console.error("Failed to fetch teacher");
        setTeacherName("Error loading teacher");
      }
    };

    /* ---------------------------------------
       Save Timetable
    --------------------------------------- */
    const handleSave = async () => {
      if (!selectedSubject || !classId) return;

      setLoading(true);

      try {
        const res = await axios.post(
          `${config.apiBaseUrl}/academic/save-timetable.php`,
          {
            class_id: classId,
            subject_id: selectedSubject,
            day_of_week: day,
            period_number: period,
            academic_year: academicYear,
          }
        );

        if (res.data.success) {
          setShow(false);
          onSuccess();
        } else {
          Swal.fire({
            icon: "error",
            title: "Schedule Conflict",
            text: res.data.message,
            confirmButtonColor: "#d33",
          });
        }
      } catch (err) {
        Swal.fire("Error", "Failed to save timetable", "error");
      }

      setLoading(false);
    };

    return (
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Assign Period ({day} - P{period})
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Subject</Form.Label>
            <Form.Select
              value={selectedSubject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              disabled={!classId}
            >
              <option value="">Select Subject</option>
              {subjects.map((sub) => (
                <option key={sub.subject_id} value={sub.subject_id}>
                  {sub.subject_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Teacher</Form.Label>
            <Form.Control type="text" value={teacherName} disabled />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!selectedSubject || loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
);

export default AssignPeriodModal;